import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useNotifications } from '../contexts/NotificationContext';
import {
  ALL_PERMISSIONS,
  getRoleBasedPermissions,
  PAGE_PERMISSIONS,
} from '../constants/permissions';
import { 
  Shield, 
  Users, 
  Settings, 
  Search, 
  UserX,
  Save,
  RefreshCw,
  Lock,
  Unlock
} from 'lucide-react';
import './PermissionsManagement.css';

const PermissionsManagement = () => {
  const { success, error } = useNotifications();
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [hasChangesByUser, setHasChangesByUser] = useState({});
  const [saving, setSaving] = useState(false);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const PAGE_GROUPS = {
    Overview: ['dashboard'],
    Management: [
      'users',
      'departments',
      'attendance_management',
      'salary_management',
      'all_requests',
      'permissions',
      'settings',
      'projects',
      'rewards',
    ],
    'My Records': ['my_profile', 'my_requests', 'my_attendance', 'my_salary', 'my_rewards'],
    Utility: ['remote_work', 'notifications', 'notification_settings'],
  };

  const getPageLabel = (pageKey) =>
    String(pageKey)
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const groupedPages = Object.entries(PAGE_GROUPS).reduce(
    (acc, [group, keys]) => ({
      ...acc,
      [group]: keys
        .filter((key) => Array.isArray(PAGE_PERMISSIONS[key]) && PAGE_PERMISSIONS[key].length > 0)
        .map((key) => ({
          key,
          label: getPageLabel(key),
          requiredPermissions: PAGE_PERMISSIONS[key],
        })),
    }),
    {},
  );

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(normalizeArray(response));
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await api.get('/permissions');
      const perms = normalizeArray(response);
      console.log('Loaded permissions (raw):', perms);
      
      // Deduplicate permissions by ID
      const uniquePerms = Array.from(
        new Map(perms.map(p => [p.id, p])).values()
      );
      console.log('Loaded permissions (deduplicated):', uniquePerms);
      setPermissions(uniquePerms);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Fallback: create default permissions structure if API fails
      const fallbackPermissions = ALL_PERMISSIONS.map((perm, index) => ({
        id: index + 1,
        ...perm,
      }));
      setPermissions(fallbackPermissions);
    }
  }, []);

  const fetchUserPermissions = useCallback(async (userId) => {
    try {
      console.log('Fetching permissions for user:', userId);
      
      // First get the user to determine their role
      const user = users.find(u => u.id === parseInt(userId));
      if (!user) {
        console.error('User not found:', userId);
        return;
      }
      
      console.log('User role:', user.role);
      
      // Admin users get all permissions
      if (user.role.toLowerCase() === 'admin') {
        console.log('Admin user - granting all permissions');
        setUserPermissions(prev => ({
          ...prev,
          [userId]: permissions
        }));
        return;
      }
      
      // Get role-based default permissions for non-admin users
      const rolePermissionNames = getRoleBasedPermissions(user.role.toLowerCase());
      console.log('Role-based permissions:', rolePermissionNames);
      
      // Convert role permission names to permission objects
      const rolePermissions = permissions.filter(p => 
        rolePermissionNames.includes(p.permission)
      );
      console.log('Role permission objects:', rolePermissions);
      
      // Try to get custom permissions from API
      let customPermissions = [];
      try {
        const response = await api.get(`/permissions/user/${userId}`);
        customPermissions = normalizeArray(response);
        console.log('Custom permissions from API:', customPermissions);
      } catch (error) {
        console.log('No custom permissions found or API error:', error.message);
      }
      
      // If custom permissions are specified, they COMPLETELY override role-based permissions
      let finalPermissions;
      
      if (customPermissions.length > 0) {
        console.log('User has custom permissions - overriding role-based permissions completely');
        // Use ONLY custom permissions, ignore role-based permissions entirely
        finalPermissions = customPermissions;
      } else {
        console.log('No custom permissions - using role-based permissions');
        // Use role-based permissions as default
        finalPermissions = rolePermissions;
      }
      
      // Deduplicate by permission ID to prevent duplicates
      const uniquePermissions = Array.from(
        new Map(finalPermissions.map(p => [p.id, p])).values()
      );
      
      console.log('Final permissions for user:', uniquePermissions);
      console.log('Custom permissions override:', customPermissions.length > 0 ? 'YES' : 'NO');
      setUserPermissions(prev => ({
        ...prev,
        [userId]: uniquePermissions
      }));
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      // Fallback to role-based permissions only
      const user = users.find(u => u.id === parseInt(userId));
      if (user) {
        if (user.role.toLowerCase() === 'admin') {
          setUserPermissions(prev => ({
            ...prev,
            [userId]: permissions
          }));
        } else {
          const rolePermissionNames = getRoleBasedPermissions(user.role.toLowerCase());
          const rolePermissions = permissions.filter(p => 
            rolePermissionNames.includes(p.permission)
          );
          setUserPermissions(prev => ({
            ...prev,
            [userId]: rolePermissions
          }));
        }
      }
    }
  }, [users, permissions]);

  const initializePermissions = useCallback(async () => {
    try {
      await api.post('/permissions/initialize');
      await fetchPermissions();
    } catch (error) {
      console.error('Error initializing permissions:', error);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchUsers(),
        fetchPermissions(),
        initializePermissions()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchPermissions, initializePermissions]);

  // Automatically load permission counts for all users when data is loaded
  useEffect(() => {
    if (!loading && users.length > 0 && permissions.length > 0) {
      const loadAllUserPermissions = async () => {
        const nonAdminUsers = users.filter(user => user.role.toLowerCase() !== 'admin');
        for (const user of nonAdminUsers) {
          if (!userPermissions[user.id]) {
            await fetchUserPermissions(user.id);
          }
        }
      };
      loadAllUserPermissions();
    }
  }, [loading, users, permissions, fetchUserPermissions]);

  const togglePagePermission = (userId, pageKey) => {
    const requiredPermissions = PAGE_PERMISSIONS[pageKey] || [];
    if (!requiredPermissions.length) {
      return;
    }

    setUserPermissions(prev => {
      const userPerms = prev[userId] || [];
      const userPermNames = new Set(userPerms.map((p) => p.permission));
      const hasPage = requiredPermissions.some((perm) => userPermNames.has(perm));
      
      let updatedPerms;
      if (hasPage) {
        updatedPerms = userPerms.filter(
          (p) => !requiredPermissions.includes(p.permission),
        );
      } else {
        const missingPermObjects = requiredPermissions
          .filter((permName) => !userPermNames.has(permName))
          .map((permName) =>
            permissions.find((permObj) => permObj.permission === permName),
          )
          .filter(Boolean);

        updatedPerms = [...userPerms, ...missingPermObjects];
      }
      
      return {
        ...prev,
        [userId]: updatedPerms
      };
    });
    setHasChangesByUser((prev) => ({ ...prev, [userId]: true }));
  };

  const saveUserPermissions = async (userId) => {
    setSaving(true);
    try {
      const user = users.find(u => u.id === parseInt(userId));
      if (!user) {
        throw new Error('User not found');
      }
      
      // Admin users don't need custom permissions saved - they get everything by default
      if (user.role.toLowerCase() === 'admin') {
        console.log('Admin user - no custom permissions to save');
        setHasChangesByUser((prev) => ({ ...prev, [userId]: false }));
        success('Admin permissions updated successfully!');
        return;
      }
      
      const userPerms = userPermissions[userId] || [];
      
      // Save ALL selected permissions (both role-based and custom)
      // User-based permissions completely override role-based permissions
      console.log('All permissions to save:', userPerms);
      
      const permissionIds = userPerms.map(p => p.id);
      console.log('Permission IDs to save:', permissionIds);
      
      await api.put(`/permissions/user/${userId}`, { permissionIds });
      
      setHasChangesByUser((prev) => ({ ...prev, [userId]: false }));
      success('Permissions updated successfully!');
    } catch (error) {
      console.error('Error saving permissions:', error);
      error('Error saving permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetUserPermissions = async (userId) => {
    if (selectedUser?.id === userId) {
      await fetchUserPermissions(userId);
      setHasChangesByUser((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const selectUser = async (user) => {
    console.log('Selecting user:', user);
    setSelectedUser(user);
    // Always fetch fresh permissions when selecting a user
    await fetchUserPermissions(user.id);
  };

  const filteredUsers = users.filter(user =>
    user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(user => user.role.toLowerCase() !== 'admin'); // Exclude admin users

  const currentUserPerms = selectedUser ? (userPermissions[selectedUser.id] || []) : [];
  const hasChanges = selectedUser ? Boolean(hasChangesByUser[selectedUser.id]) : false;
  const currentUserPermNames = new Set(currentUserPerms.map((p) => p.permission));

  const getEnabledPageCount = (userId) => {
    const perms = userPermissions[userId] || [];
    const names = new Set(perms.map((p) => p.permission));
    return Object.keys(PAGE_PERMISSIONS).filter((pageKey) =>
      PAGE_PERMISSIONS[pageKey].some((requiredPerm) => names.has(requiredPerm)),
    ).length;
  };
  
  console.log('Selected user:', selectedUser);
  console.log('All permissions:', permissions);
  console.log('Grouped pages:', groupedPages);
  console.log('Current user permissions:', currentUserPerms);

  if (loading) {
    return (
      <div className="permissions-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading permissions management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="permissions-management">
      <div className="page-header">
        <h1><Shield size={24} /> Permissions Management</h1>
        <p>Manage what users can and cannot do in the system</p>
        <small className="admin-note">Note: Admin users have all permissions by default and are not shown here</small>
        <button className="refresh-btn" onClick={() => {
          fetchUsers();
          fetchPermissions();
          if (selectedUser) {
            fetchUserPermissions(selectedUser.id);
          }
        }}>
          <RefreshCw size={20} />
          Refresh
        </button>
      </div>

      <div className="permissions-content">
        <div className="users-panel">
          <div className="panel-header">
            <h2>
              <Users size={20} />
              Users
            </h2>
            <div className="search-container">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="users-list">
            {filteredUsers.map(user => (
              <div
                key={user.id}
                className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                onClick={() => selectUser(user)}
              >
                <div className="user-avatar">
                  {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                </div>
                <div className="user-info">
                  <div className="user-name">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="user-details">
                    @{user.username} • {user.email}
                  </div>
                  <div className="user-role">
                    <span className={`role-badge ${user.role.toLowerCase()}`}>
                      {user.role}
                    </span>
                    {!user.isActive && <span className="status-badge inactive">Inactive</span>}
                  </div>
                </div>
                <div className="user-permission-count">
                  {userPermissions[user.id] ? (
                    <>{getEnabledPageCount(user.id)} pages</>
                  ) : (
                    <span className="loading-count">Loading...</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="permissions-panel">
          {selectedUser ? (
            <>
              <div className="panel-header">
                <h2>
                  <Settings size={20} />
                  Page Access for {selectedUser.firstName} {selectedUser.lastName}
                </h2>
                <div className="panel-actions">
                  {hasChanges && (
                    <button
                      className="save-btn"
                      onClick={() => saveUserPermissions(selectedUser.id)}
                      disabled={saving}
                    >
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  )}
                  {hasChanges && (
                    <button
                      className="reset-btn"
                      onClick={() => resetUserPermissions(selectedUser.id)}
                    >
                      <RefreshCw size={16} />
                      Reset
                    </button>
                  )}
                </div>
              </div>

              <div className="permissions-list">
                {permissions.length === 0 ? (
                  <div className="no-permissions">
                    <p>No permissions available. Please initialize the permissions system.</p>
                    <button 
                      className="init-btn"
                      onClick={initializePermissions}
                    >
                      Initialize Permissions
                    </button>
                  </div>
                ) : (
                  Object.entries(groupedPages).map(([category, categoryPages]) => (
                    <div key={category} className="permission-section">
                      <div className="section-header">
                        <h4>{category}</h4>
                        <span className="section-count">
                          {categoryPages.filter((pageItem) =>
                            pageItem.requiredPermissions.some((perm) =>
                              currentUserPermNames.has(perm),
                            ),
                          ).length} / {categoryPages.length}
                        </span>
                      </div>
                      
                      <div className="permissions-grid">
                        {categoryPages.map((pageItem, idx) => {
                          const hasPermission = pageItem.requiredPermissions.some((perm) =>
                            currentUserPermNames.has(perm),
                          );
                          const user = selectedUser;
                          const isAdmin = user?.role.toLowerCase() === 'admin';
                          const rolePermissionNames = user && !isAdmin ? getRoleBasedPermissions(user.role.toLowerCase()) : [];
                          const isRoleBased = !isAdmin && pageItem.requiredPermissions.some((perm) => rolePermissionNames.includes(perm));
                          
                          console.log(`Rendering page access: ${pageItem.key} in category: ${category}`);
                          
                          return (
                            <div
                              key={`${category}-${pageItem.key}-${idx}`}
                              data-page-key={pageItem.key}
                              className={`permission-card ${hasPermission ? 'granted' : 'denied'} ${isRoleBased ? 'role-based' : 'custom'}`}
                            >
                              <div className="permission-toggle">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={hasPermission}
                                    onChange={() => togglePagePermission(selectedUser.id, pageItem.key)}
                                    disabled={isAdmin} // Admin permissions can't be changed
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </div>
                              <div className="permission-content">
                                <div className="permission-name">
                                  {pageItem.label}
                                  {isRoleBased && (
                                    <span className="role-indicator">Role</span>
                                  )}
                                  {isAdmin && (
                                    <span className="admin-indicator">Admin</span>
                                  )}
                                </div>
                                <div className="permission-description">Page access ({pageItem.requiredPermissions.length} linked permissions)</div>
                              </div>
                              <div className="permission-status">
                                {hasPermission ? (
                                  <span className="status-badge granted">
                                    <Lock size={12} />
                                    Granted
                                  </span>
                                ) : (
                                  <span className="status-badge denied">
                                    <Unlock size={12} />
                                    Denied
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="no-user-selected">
              <UserX size={48} />
              <h3>No User Selected</h3>
              <p>Select a user from the list to manage their permissions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsManagement;
